import { readChecks, readFollowUps, type AiCheck } from '@d-orca/shared/ai-answer';
import { readLink, type AiLink } from '@d-orca/shared/navigation';
import type { AiChatStreamEvent, AiDrawing, AiMessage, AiMessageRole } from '@apis/aiAgent';
import { AiRateLimitedError, AiTurnInProgressError } from '@apis/aiAgentErrors';

/**
 * Shared helpers for the AI Agent chat surfaces (the dashboard `AiAgentCard`
 * and the full `AiAgentPage`). Both consume the same SSE stream, so the
 * security-sensitive URL handling, the SSE vocabulary → i18n-key mappings and
 * the last-assistant-message updater live here rather than being duplicated.
 */

/**
 * A rendered message: the wire shape plus the annotations only the client
 * knows. `id` is optional here and nowhere else — the page appends the two
 * bubbles of a turn before the stream has opened, so for the moment between
 * "send" and the `metadata` frame they have no server identity at all.
 *
 * `truncated`, `dataGap`, `stopped` and `unsaved` are per-TURN facts the store does not
 * keep, so they survive only for the life of the open thread. That is the
 * right lifetime: they qualify an answer the user is reading now.
 */
export interface IChatMessage extends Omit<AiMessage, 'id'> {
  /** Absent only between the optimistic append and the `metadata` frame. */
  id?: string;
  /** The model's output limit stopped this answer mid-way: it is CUT OFF. */
  truncated?: boolean;
  /**
   * A tool failed mid-turn, so the answer is written around missing data. Live
   * only; a reopened thread learns the same from its receipt — read both
   * through `hasDataGap`.
   */
  dataGap?: boolean;
  /** The reader stopped the turn before it finished, so whatever is here is all there is. */
  stopped?: boolean;
  /**
   * The turn has not (successfully) written this message to the thread, so
   * there is nothing on the server to rate. Set on the bubble the page
   * appends and cleared on `done` — a turn that ERRORS never clears it,
   * because a failed turn persists no answer and rating it would 404 for a
   * reason no user could interpret. Restored messages never carry it: coming
   * back from the store is proof they are saved.
   */
  unsaved?: boolean;
}

/**
 * The live status line: which tool, and whether the frame said that call
 * FAILED rather than started. A failing tool produces two frames with the same
 * label, so `failed` is a state change on one line — rendering both as the
 * same "…in progress" sentence would read as the assistant doing the same
 * thing twice.
 */
export interface IChatStatus {
  label: string;
  failed: boolean;
  /**
   * One SECTION of a composite could not be read — `query_site_brief` reports
   * this per section. Distinct from `failed`, which is the whole call: the
   * brief succeeded and one of its six sources did not. The line reads the same
   * either way today; the flag is here so a client can mark it without the
   * backend needing a new frame type.
   */
  degraded?: boolean;
}

/**
 * Stamp the ids from a turn's `metadata` frame onto the two bubbles the page
 * appended before the stream opened — the last two entries, question then
 * answer.
 *
 * Position is used here and ONLY here: within one client-side turn the page
 * knows exactly which two rows it just pushed. The server is addressed by id,
 * never by position, which is the whole reason these ids exist.
 */
export function attachTurnIds(
  messages: IChatMessage[],
  questionId: string | undefined,
  answerId: string | undefined,
): IChatMessage[] {
  const answerAt = messages.length - 1;
  const questionAt = messages.length - 2;
  const answer = messages[answerAt];
  const question = messages[questionAt];
  // Anything else means the transcript moved under us (a reset, a restore);
  // stamping ids onto the wrong rows would be worse than not stamping them.
  if (answer?.role !== 'assistant' || question?.role !== 'user') return messages;
  const next = [...messages];
  next[answerAt] = { ...answer, id: answerId };
  next[questionAt] = { ...question, id: questionId };
  return next;
}

/**
 * Tool id → i18n key for the live status line.
 *
 * The raw id (`query_issues`) is an implementation detail and must never be
 * shown; an unrecognised one falls back to a neutral "working" sentence rather
 * than leaking the name of a tool the user has no model of. Every id in
 * `AI_TOOL_IDS` (`@d-orca/shared/ai-answer`) has an entry — the list the
 * backend's registry is pinned to, and the test of this map: a tool that
 * shipped without one showed "working on it" for the whole of its read.
 *
 * Exported for that test alone; callers go through `aiStatusLabelKey`.
 */
export const STATUS_LABEL_KEYS: Readonly<Record<string, string>> = {
  query_issues: 'aiAgent.status.queryIssues',
  query_schedule: 'aiAgent.status.querySchedule',
  query_activities: 'aiAgent.status.queryActivities',
  query_workforce: 'aiAgent.status.queryWorkforce',
  query_safety: 'aiAgent.status.querySafety',
  get_site_info: 'aiAgent.status.getSiteInfo',
  query_daily_route: 'aiAgent.status.queryDailyRoute',
  query_productivity: 'aiAgent.status.queryProductivity',
  query_attendance: 'aiAgent.status.queryAttendance',
  query_deliveries: 'aiAgent.status.queryDeliveries',
  query_stock: 'aiAgent.status.queryStock',
  query_documents: 'aiAgent.status.queryDocuments',
  query_weather: 'aiAgent.status.queryWeather',
  query_open_work: 'aiAgent.status.queryOpenWork',
  query_breakdown: 'aiAgent.status.queryBreakdown',
  get_page_guide: 'aiAgent.status.getPageGuide',
  render_table: 'aiAgent.status.renderTable',
  render_chart: 'aiAgent.status.renderChart',
  // The two output tools travel beside `respond`, after the wait, so their
  // lines rarely show — but a line that does show must not be the fallback.
  offer_link: 'aiAgent.status.offerLink',
  suggest_followups: 'aiAgent.status.suggestFollowups',
  show_screenshot: 'aiAgent.status.showScreenshot',
  // `query_site_brief` announces itself once, then once PER SECTION — six
  // sources is the slowest turn the assistant runs, and one line for all of
  // them is a progress bar that does not move. Without an entry per section the
  // chip falls back to "working on it", which is not wrong but says nothing.
  query_site_brief: 'aiAgent.status.querySiteBrief',
  'brief.safety': 'aiAgent.status.briefSafety',
  'brief.scheduleChanges': 'aiAgent.status.briefScheduleChanges',
  'brief.issues': 'aiAgent.status.briefIssues',
  'brief.activityVsPlan': 'aiAgent.status.briefActivityVsPlan',
  'brief.todaySchedule': 'aiAgent.status.briefTodaySchedule',
  'brief.weather': 'aiAgent.status.briefWeather',
};

/** i18n key for the status line shown while `toolName` runs. */
export function aiStatusLabelKey(toolName: string): string {
  return STATUS_LABEL_KEYS[toolName] ?? 'aiAgent.status.fallback';
}

/**
 * Error code → i18n key.
 *
 * The `error` frame carries a code precisely so the UI can speak Japanese; its
 * `message` is internal English prose and is dropped at the API boundary.
 * Anything unrecognised gets the generic string — a code the frontend has never
 * heard of is not something to show the user verbatim.
 */
const ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  AI_TURN_FAILED: 'aiAgent.error.turnFailed',
};

/** i18n key for the user-facing text of an `error` frame's `code`. */
export function aiErrorMessageKey(code: string): string {
  return ERROR_MESSAGE_KEYS[code] ?? 'aiAgent.error.generic';
}

/**
 * The notice for a `done{aborted}`: the deadline ended the turn and the server
 * kept no answer. An error rather than the stop notice — the reader did not
 * choose it, and the stop notice says they did.
 */
export const AI_CUT_OFF_MESSAGE_KEY = 'aiAgent.error.cutOff';

/**
 * Settle the answer being written on its turn's `done`.
 *
 * The turn persists the answer before its closing frames leave the server, so
 * `unsaved` clears here — except for a turn that failed (`errored`), produced
 * nothing, or was ABORTED by its deadline: none of those stored an answer, and
 * rating one would 404 for a reason no reader could interpret.
 */
export function settleAnswer<T extends IChatMessage>(
  last: T,
  done: { truncated: boolean; aborted?: boolean },
  errored: boolean,
): T {
  return {
    ...last,
    truncated: done.truncated,
    unsaved: errored || done.aborted === true || last.content.length === 0,
  };
}

/**
 * i18n key for a send that failed before or while streaming. The two refusals —
 * another answer still being written in the thread, or too many questions too
 * fast — have sentences of their own; everything else is the surface's generic
 * one.
 */
export function aiSendFailureKey(err: unknown, genericKey: string): string {
  if (err instanceof AiTurnInProgressError) return 'aiAgent.error.turnInProgress';
  if (err instanceof AiRateLimitedError) return 'aiAgent.error.rateLimit';
  return genericKey;
}

/**
 * Take back the two bubbles a REFUSED ask appended — the question and its empty
 * answer. The server stored neither, so leaving them would show a question the
 * reopened thread does not have.
 *
 * Only a refused ask: any other failure may have stored the question. The role
 * guard is `attachTurnIds`'s: if the transcript moved under the send (a reset,
 * a restore), the last two rows are not this ask's.
 *
 * Not a rate-limited one, though it stored nothing either: it is the FIRST
 * question of a new chat as often as not, and taking back the only two bubbles
 * empties the transcript — and the error line with it, which lives inside it.
 * The question stays on screen with the reason it went unanswered under it.
 */
export function dropRefusedTurn<T extends { role: AiMessageRole }>(messages: T[], err: unknown): T[] {
  if (!(err instanceof AiTurnInProgressError)) return messages;
  const answer = messages.at(-1);
  const question = messages.at(-2);
  if (answer?.role !== 'assistant' || question?.role !== 'user') return messages;
  return messages.slice(0, -2);
}

// Exactly one leading slash, not followed by another slash or a backslash.
// Rejects protocol-relative `//evil.com` (the History API would resolve it to
// an external origin → open redirect) and the `/\evil.com` backslash variant
// that some browsers normalise to `//`.
const INTERNAL_PATH = /^\/(?![/\\])/;

/**
 * An in-app path from a chat surface, or `null` for anything else — which the
 * caller drops rather than navigates to.
 *
 * In-app ONLY. The assistant's own links are built from the navigation catalog
 * (`@d-orca/shared/navigation`) and never travel as URL strings, so nothing on a
 * chat surface has a reason to open another origin — and an http(s) arm here
 * would be the one place a URL written into an answer could still become one.
 * The dashboard card's "view in full view" nudge is the caller.
 */
export function resolveAiLink(url: string): string | null {
  const trimmed = url.trim();
  return INTERNAL_PATH.test(trimmed) ? trimmed : null;
}

/**
 * Append one link to the message being written, in the order it was offered —
 * the order the model ranked them in, most specific first.
 */
export function appendLink<T extends object>(
  message: T & { links?: AiLink[] },
  link: AiLink,
): T & { links: AiLink[] } {
  return { ...message, links: [...(message.links ?? []), link] };
}

/**
 * The links a message offered, as today's catalog reads them. A thread written
 * weeks ago may name a param since renamed or a page since removed; what no
 * longer parses is dropped rather than opened wrongly (`readLink`).
 */
export function linksOf(message: Pick<AiMessage, 'links'>): AiLink[] {
  return (message.links ?? []).flatMap((link) => {
    const read = readLink(link);
    return read === null ? [] : [read];
  });
}

/**
 * Put the turn's receipt on the message being written. SET, not appended: the
 * frame carries the whole receipt, once, already folded worst-outcome-first on
 * the server — the same fold that stored it.
 */
export function withChecks<T extends object>(message: T, checks: AiCheck[]): T & { checks: AiCheck[] } {
  return { ...message, checks };
}

/**
 * The receipt a message carries, as this build reads it — a source it no
 * longer knows is dropped rather than failing the line (`readChecks`).
 */
export function checksOf(message: Pick<AiMessage, 'checks'>): AiCheck[] {
  return readChecks(message.checks);
}

/**
 * Whether the answer was written around data it could not read. Live, a failed
 * tool frame says so (`dataGap`); on a reopened thread only the stored receipt
 * can — `dataGap` is client-only and did not survive the reload, which left a
 * reopened answer reading as complete.
 */
export function hasDataGap(message: Pick<IChatMessage, 'checks' | 'dataGap'>): boolean {
  return message.dataGap === true || checksOf(message).some((check) => check.outcome === 'failed');
}

/**
 * Put the suggested next questions on the message being written, REPLACING any
 * earlier ones: a second call of the tool is what the model settled on.
 */
export function withFollowUps<T extends object>(message: T, questions: string[]): T & { followUps: string[] } {
  return { ...message, followUps: questions };
}

/** The next questions a message suggests, read leniently (`readFollowUps`). */
export function followUpsOf(message: Pick<AiMessage, 'followUps'>): string[] {
  return readFollowUps(message.followUps);
}

/**
 * A `table`, `chart` or `image` frame as the drawing it adds, keeping the id
 * its marker in the answer names — without it the drawing could only follow
 * the prose.
 */
export function drawingFrom(frame: Extract<AiChatStreamEvent, { type: 'table' | 'chart' | 'image' }>): AiDrawing {
  const id = frame.id === undefined ? {} : { id: frame.id };
  switch (frame.type) {
    case 'table':
      return { kind: 'table', table: frame.table, ...id };
    case 'chart':
      return { kind: 'chart', chart: frame.chart, ...id };
    case 'image':
      return { kind: 'image', image: frame.image, ...id };
  }
}

/**
 * Append one drawing to the message being written. APPENDED, not replaced: an
 * answer that draws twice — the morning brief draws one table per section —
 * showed only its last table once the thread was reopened.
 */
export function appendDrawing<T extends object>(
  // `T & {...}` rather than a constrained `T`: a message that has drawn nothing
  // yet has no `drawings` key at all, and a constraint naming only that key
  // leaves such a message with nothing in common to infer from.
  message: T & { drawings?: AiDrawing[] },
  drawing: AiDrawing,
): T & { drawings: AiDrawing[] } {
  return { ...message, drawings: [...(message.drawings ?? []), drawing] };
}

/**
 * Everything a message put on screen, in the order it was drawn. The two
 * singular fields are threads written before `drawings` existed — rendered,
 * never written — and come after, which is where they rendered before too.
 */
export function drawingsOf(
  message: Pick<AiMessage, 'drawings' | 'table' | 'chart'>,
): AiDrawing[] {
  const legacy: AiDrawing[] = [];
  if (message.table) legacy.push({ kind: 'table', table: message.table });
  if (message.chart) legacy.push({ kind: 'chart', chart: message.chart });
  return [...(message.drawings ?? []), ...legacy];
}

/** The frames that write the answer itself, rather than the turn around it. */
export type AiAnswerFrame = Extract<
  AiChatStreamEvent,
  { type: 'text' | 'table' | 'chart' | 'image' | 'link' | 'checks' | 'followUps' }
>;

const ANSWER_FRAME_TYPES: ReadonlySet<AiChatStreamEvent['type']> = new Set([
  'text',
  'table',
  'chart',
  'image',
  'link',
  'checks',
  'followUps',
]);

/** Whether a frame writes the answer itself (`withAnswerFrame`) rather than the turn around it. */
export function isAnswerFrame(event: AiChatStreamEvent): event is AiAnswerFrame {
  return ANSWER_FRAME_TYPES.has(event.type);
}

/**
 * The answer being written, with one more of its frames applied: prose
 * APPENDED (paragraph breaks between rounds are already in it), drawings and
 * links appended in order, the receipt and the follow-ups set.
 *
 * One function for both chat surfaces. Each read the same frames into the same
 * message with its own switch, and two switches over one wire is how one of them
 * learns a new frame and the other does not — the card once dropped charts that
 * way. What differs per surface (the status line, errors, `done`) stays with it.
 */
export function withAnswerFrame<T extends IChatMessage>(message: T, frame: AiAnswerFrame): T {
  switch (frame.type) {
    case 'text':
      return { ...message, content: message.content + frame.content };
    case 'table':
    case 'chart':
    case 'image':
      return appendDrawing(message, drawingFrom(frame));
    case 'link':
      return appendLink(message, frame.link);
    // Both arrive before the answer is complete and are held back until it
    // is, like links — the transcript decides when to show them.
    case 'checks':
      return withChecks(message, frame.checks);
    case 'followUps':
      return withFollowUps(message, frame.questions);
  }
}

/**
 * Replace the last message in `messages` with `updater(last)`, but only when
 * it is an assistant message. Returns the same array reference when there is
 * nothing to update (empty list, or last message is the user's) so React can
 * skip a re-render. The role guard prevents an out-of-order SSE event from
 * clobbering a user bubble.
 */
export function replaceLastAssistant<T extends { role: AiMessageRole }>(
  messages: T[],
  updater: (last: T) => T,
): T[] {
  if (messages.length === 0) return messages;
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return messages;
  const next = [...messages];
  next[next.length - 1] = updater(last);
  return next;
}

/**
 * Has any answer in the thread produced something a PDF could carry — prose, a
 * table or a chart?
 *
 * Here rather than in the page because it is a question about the MESSAGE SHAPE,
 * which is this module's subject, and because the page it came from was at the
 * file-size limit. It drives the export trigger's visibility in the composer.
 */
export function hasExportableAnswer(messages: IChatMessage[]): boolean {
  return messages.some(
    (message) =>
      message.role === 'assistant' &&
      (message.content.length > 0 ||
        (message.drawings ?? []).length > 0 ||
        Boolean(message.table) ||
        Boolean(message.chart)),
  );
}
